import { useEffect, useState, useRef, type MutableRefObject } from 'react';
import { Popup, Form, TextArea, Button, Toast, ImageUploader, Dialog } from 'antd-mobile';
import type { ImageUploadItem } from 'antd-mobile/es/components/image-uploader';
import { useTranslation } from 'react-i18next';
import {
  supabase,
  validateSOSForm,
  uploadFile,
  generateUUID,
  toGeoPointWKT,
  submitSOSEvent,
  SOS_EVENT_TYPES,
  reverseGeocodeClient,
} from '@ubus/shared';
import type { SOSFormData } from '@ubus/shared';
import { EnvironmentOutline } from 'antd-mobile-icons';

const DRIVER_SOS_TYPES = SOS_EVENT_TYPES.filter((t) => t !== 'Vehicle Not Appearing');

interface SOSButtonProps {
  tripId?: string;
  variant?: 'fab' | 'map';
}

function fileExtension(file: File): string {
  const fromName = file.name.split('.').pop();
  if (fromName && fromName !== file.name) return fromName;
  return file.type.split('/')[1] || 'jpg';
}

async function resolvePhotoFile(
  items: ImageUploadItem[],
  selectedFileRef: MutableRefObject<File | null>
): Promise<File | null> {
  if (selectedFileRef.current) return selectedFileRef.current;
  if (items.length === 0) return null;
  const last = items[items.length - 1];
  if (last.extra instanceof File) return last.extra;
  if (last.url?.startsWith('blob:')) {
    const res = await fetch(last.url);
    const blob = await res.blob();
    return new File([blob], `photo.${blob.type.split('/')[1] || 'jpg'}`, { type: blob.type || 'image/jpeg' });
  }
  return null;
}

function SOSButton({ tripId, variant = 'fab' }: SOSButtonProps) {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form] = Form.useForm();
  const [selectedEventType, setSelectedEventType] = useState('');
  const [imageItems, setImageItems] = useState<ImageUploadItem[]>([]);
  const [locationLabel, setLocationLabel] = useState('');
  const [locationWkt, setLocationWkt] = useState<string | null>(null);
  const selectedFileRef = useRef<File | null>(null);

  useEffect(() => {
    if (!visible) return;
    setLocationLabel(t('sos.detectingLocation'));
  }, [visible, t]);

  const refreshLocation = async () => {
    setLocationLabel(t('sos.detectingLocation'));
    if (!navigator.geolocation) {
      setLocationLabel(t('sos.locationUnavailable'));
      return;
    }
    try {
      const position = await new Promise<GeolocationPosition>((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 8000,
        });
      });
      const { latitude, longitude } = position.coords;
      setLocationWkt(toGeoPointWKT(latitude, longitude));
      const address = await reverseGeocodeClient(latitude, longitude);
      setLocationLabel(address?.trim() || t('sos.addressUnavailable'));
    } catch {
      setLocationLabel(t('sos.couldNotDetect'));
      setLocationWkt(null);
    }
  };

  const handleActivate = () => {
    if (!tripId) {
      Toast.show({ icon: 'fail', content: t('sos.openActiveTask') });
      return;
    }
    if (navigator.vibrate) navigator.vibrate(200);
    setVisible(true);
    refreshLocation();
  };

  const resetForm = () => {
    form.resetFields();
    setSelectedEventType('');
    selectedFileRef.current = null;
    setImageItems([]);
    setLocationLabel(t('sos.detectingLocation'));
    setLocationWkt(null);
  };

  const submitSOS = async () => {
    const values = form.getFieldsValue() as SOSFormData;
    values.event_type = selectedEventType;

    const validation = validateSOSForm(values);
    if (!validation.isValid) {
      const messages = validation.errors.map((e) =>
        e.field === 'event_type' ? t('errors.sos.eventTypeRequired') : e.message
      );
      Toast.show({ icon: 'fail', content: messages.join(', ') });
      return;
    }

    if (!tripId) return;

    setSubmitting(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        Toast.show({ icon: 'fail', content: t('sos.signInFirst') });
        return;
      }

      let photoUrl: string | null = null;
      if (imageItems.length > 0) {
        const fileToUpload = await resolvePhotoFile(imageItems, selectedFileRef);
        if (!fileToUpload) {
          Toast.show({ icon: 'fail', content: t('sos.photoNotReady') });
          return;
        }
        photoUrl = await uploadFile('sos', `sos/${generateUUID()}.${fileExtension(fileToUpload)}`, fileToUpload);
        if (!photoUrl) {
          Toast.show({ icon: 'fail', content: t('sos.photoUploadFailed') });
          return;
        }
      }

      const { error } = await submitSOSEvent({
        tripId,
        reporterRole: 'driver',
        eventType: values.event_type,
        description: values.description || null,
        photoUrl,
        location: locationWkt,
      });

      if (error) {
        Toast.show({ icon: 'fail', content: error.message });
      } else {
        Toast.show({ icon: 'success', content: t('sos.sent') });
        resetForm();
        setVisible(false);
      }
    } catch (err: unknown) {
      Toast.show({
        icon: 'fail',
        content: err instanceof Error ? err.message : t('sos.sendFailed'),
      });
    } finally {
      setSubmitting(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedEventType) {
      Toast.show({ icon: 'fail', content: t('sos.selectEventType') });
      return;
    }

    Dialog.show({
      title: t('sos.confirmTitle'),
      closeOnAction: true,
      actions: [
        [
          { key: 'cancel', text: t('common.cancel') },
          { key: 'confirm', text: t('common.confirm'), bold: true, onClick: submitSOS },
        ],
      ],
    });
  };

  const sosLabel = t('sos.label');

  return (
    <>
      {variant === 'map' ? (
        <button type="button" className="driver-sos-map-btn" onClick={handleActivate}>
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3C8.5 3 6 5.5 6 9c0 4.5 6 12 6 12s6-7.5 6-12c0-3.5-2.5-6-6-6Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="9" r="2" fill="currentColor" />
          </svg>
          <span>{sosLabel}</span>
        </button>
      ) : (
        <button type="button" className="driver-sos-fab" onClick={handleActivate}>
          <svg className="driver-sos-fab__icon" width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path
              d="M12 3C8.5 3 6 5.5 6 9c0 4.5 6 12 6 12s6-7.5 6-12c0-3.5-2.5-6-6-6Z"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <circle cx="12" cy="9" r="2" fill="currentColor" />
          </svg>
          <span>{sosLabel}</span>
        </button>
      )}

      <Popup
        visible={visible}
        onMaskClick={() => !submitting && setVisible(false)}
        bodyStyle={{ borderTopLeftRadius: 20, borderTopRightRadius: 20, padding: 20, minHeight: '60vh' }}
      >
        <div className="driver-sos-popup-header">
          <div style={{ width: 40, height: 4, background: '#E5E7EB', borderRadius: 2, margin: '0 auto 16px' }} />
          <div className="driver-sos-popup-title">{t('sos.emergencyTitle')}</div>
          <p className="driver-sos-popup-subtitle">{t('sos.emergencySubtitle')}</p>
        </div>

        <Form form={form} layout="vertical" footer={null}>
          <div className="driver-sos-types">
            {DRIVER_SOS_TYPES.map((type) => (
              <button
                key={type}
                type="button"
                className={`driver-sos-type-chip ${selectedEventType === type ? 'driver-sos-type-chip--active' : ''}`}
                onClick={() => setSelectedEventType(type)}
              >
                {t(`sos.types.${type}`, { defaultValue: type })}
              </button>
            ))}
          </div>

          <Form.Item name="description" label={t('sos.descriptionLabel')}>
            <TextArea placeholder={t('sos.descriptionPlaceholder')} rows={3} maxLength={500} showCount />
          </Form.Item>

          <Form.Item label={t('sos.pictureLabel')}>
            <ImageUploader
              value={imageItems}
              onChange={(items) => {
                setImageItems(items);
                if (items.length === 0) selectedFileRef.current = null;
              }}
              upload={async (file) => {
                selectedFileRef.current = file;
                return { url: URL.createObjectURL(file), extra: file };
              }}
              maxCount={1}
              accept="image/*"
            />
          </Form.Item>

          <div className="driver-sos-location">
            <div className="driver-sos-location-label">
              <EnvironmentOutline /> {t('sos.locationLabel')}
            </div>
            <div className="driver-sos-location-row">
              <span className="driver-sos-location-text">{locationLabel}</span>
              <button type="button" className="driver-sos-location-refresh" onClick={refreshLocation}>
                {t('common.refresh')}
              </button>
            </div>
          </div>

          <div className="driver-sos-actions">
            <Button
              className="driver-sos-cancel"
              onClick={() => { resetForm(); setVisible(false); }}
              disabled={submitting}
            >
              {t('common.cancel')}
            </Button>
            <Button
              className="driver-sos-confirm"
              color="danger"
              loading={submitting}
              onClick={handleSubmit}
            >
              {t('sos.sendSos')}
            </Button>
          </div>
        </Form>
      </Popup>
    </>
  );
}

export default SOSButton;
